import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Network, Loader2, Search, User, HardHat, MapPin, Zap, LayoutGrid, ExternalLink, FileText, Unlock, Presentation } from "lucide-react";
import { useNavigate } from "react-router-dom";

const eur0 = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });

const STATUS = {
  completata: { label: "Completata", cls: "border-success/40 text-success bg-success/5" },
  in_lavorazione: { label: "In lavorazione", cls: "border-primary/40 text-primary bg-primary/5" },
  bozza: { label: "Bozza", cls: "border-muted-foreground/30 text-muted-foreground bg-secondary/40" },
};

const FILTERS = [
  ["", "Tutte"],
  ["in_lavorazione", "In lavorazione"],
  ["completata", "Completate"],
  ["bozza", "Bozza"],
];

export default function ReteConsulenze() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, completate: 0, in_lavorazione: 0, bozza: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [unlocking, setUnlocking] = useState(null);
  const navigate = useNavigate();

  const unlock = async (it) => {
    if (!window.confirm("Sbloccare la pratica? Entra in pipeline assegnata a te e il venditore torna Fier S.p.A.")) return;
    setUnlocking(it.id);
    try {
      const { data } = await api.post(`/free-users/${it.lead_id}/to-lead`);
      toast.success("Pratica sbloccata e assegnata a te");
      navigate(`/leads/${data.lead_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sblocco non riuscito");
    } finally { setUnlocking(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      const { data } = await api.get("/rete/consulenze", { params });
      setItems(data.items || []);
      setStats(data.stats || { total: 0, completate: 0, in_lavorazione: 0, bozza: 0 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento consulenze rete");
    } finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="rete-consulenze-page">
      <div>
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
          <Network className="h-6 w-6 text-primary" strokeWidth={1.5} /> Consulenze Rete
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Pratiche generate dagli agenti su <b>rete.torri.solar</b>. Agente e installatore possono prezzarle e generare la proposta dal portale; con "Sblocca e lavora" la pratica entra in pipeline e il venditore torna Fier S.p.A.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="rete-consulenze-stats">
        {[
          ["Totali", stats.total, "text-foreground"],
          ["In lavorazione", stats.in_lavorazione, "text-primary"],
          ["Completate", stats.completate, "text-success"],
          ["Bozze", stats.bozza, "text-muted-foreground"],
        ].map(([label, val, cls]) => (
          <Card key={label} className="p-4">
            <div className={`text-2xl font-bold ${cls}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, agente, installatore, comune…"
            className="pl-9" data-testid="rete-consulenze-search" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map(([val, label]) => (
            <button key={val || "all"} type="button" onClick={() => setStatus(val)}
              data-testid={`rete-filter-${val || "all"}`}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${status === val ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="rete-consulenze-empty">
          Nessuna consulenza rete trovata.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="rete-consulenze-list">
          {items.map((it) => {
            const s = STATUS[it.status] || STATUS.bozza;
            const summ = it.summary || {};
            const disp = summ.disposizione_falde || [];
            const consulenzaPath = it.consulenza_url && it.consulenza_url.includes("/c/")
              ? it.consulenza_url.slice(it.consulenza_url.indexOf("/c/")) : it.consulenza_url;
            return (
              <Card key={it.id} className="p-4" data-testid={`rete-consulenza-${it.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-base flex items-center gap-1.5">
                        <User className="h-4 w-4 text-muted-foreground" /> {summ.cliente || (it.consumer?.name) || "—"}
                      </span>
                      <Badge variant="outline" className={`text-[11px] font-normal ${s.cls}`} data-testid={`rete-status-${it.id}`}>{s.label}</Badge>
                      <Badge variant="secondary" className="text-[11px] font-normal">{it.tool_label || it.tool}</Badge>
                    </div>
                    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground">
                      {it.agent?.name && <span className="flex items-center gap-1"><User className="h-3 w-3" /> Agente: {it.agent.name}</span>}
                      {it.installer?.ragione_sociale && <span className="flex items-center gap-1"><HardHat className="h-3 w-3" /> {it.installer.ragione_sociale}</span>}
                      {summ.comune && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {summ.comune}</span>}
                    </div>
                    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs">
                      {summ.potenza_kwp != null && <span className="flex items-center gap-1"><Zap className="h-3 w-3 text-primary" /> {summ.potenza_kwp} kWp</span>}
                      {summ.n_pannelli != null && <span>{summ.n_pannelli} pannelli</span>}
                      {summ.superficie_mq != null && <span>{eur0(summ.superficie_mq)} m²</span>}
                      {summ.orientamento && <span className="flex items-center gap-1"><LayoutGrid className="h-3 w-3 text-primary" /> {summ.orientamento}</span>}
                    </div>
                    {disp.length > 0 && (
                      <div className="text-[11px] text-muted-foreground" data-testid={`rete-disposizione-${it.id}`}>
                        Disposizione: {disp.map((d, i) => (
                          <span key={i} className="mr-2">falda {d.falda_id || i + 1}: [{(d.rows || []).join(", ")}] {d.orientamento ? `· ${d.orientamento}` : ""}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {consulenzaPath && (
                      <a href={consulenzaPath} target="_blank" rel="noopener noreferrer" data-testid={`rete-consulenza-link-${it.id}`}>
                        <Button variant="outline" size="sm"><FileText className="h-4 w-4 mr-1.5" /> Vedi consulenza</Button>
                      </a>
                    )}
                    {it.outlook_url && (
                      <a href={it.outlook_url} target="_blank" rel="noopener noreferrer" data-testid={`rete-outlook-link-${it.id}`}>
                        <Button variant="outline" size="sm"><Presentation className="h-4 w-4 mr-1.5" /> Vedi proposta</Button>
                      </a>
                    )}
                    {it.lead_id && (it.rete_unlocked ? (
                      <Button size="sm" variant="secondary" onClick={() => navigate(`/leads/${it.lead_id}`)} data-testid={`rete-open-lead-${it.id}`}>Apri lead in pipeline</Button>
                    ) : (
                      <Button size="sm" onClick={() => unlock(it)} disabled={unlocking === it.id} data-testid={`rete-unlock-${it.id}`}>
                        {unlocking === it.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Unlock className="h-4 w-4 mr-1.5" />} Sblocca e lavora
                      </Button>
                    ))}
                    {it.public_token && (
                      <a href={`/rete/p/${it.public_token}`} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1" data-testid={`rete-public-link-${it.id}`}>
                        <ExternalLink className="h-3 w-3" /> Link pratica
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
